import { authService } from '../../services/auth';
import { adminApi, type AdminRevenueOverviewDto } from '../../services/admin.api';
import { onLocaleChange, t } from '../../services/i18n';
import { esc, formatCurrencyFromCents } from './admin-shared';

export class RcAdminRevenue extends HTMLElement {
  private overview: AdminRevenueOverviewDto | null = null;
  private isLoading = false;
  private error = '';
  private disposeLocaleChange?: () => void;

  connectedCallback(): void {
    this.render();
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    if (authService.isAuthenticated) {
      void this.loadOverview();
    }
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
  }

  private async loadOverview(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    this.render();

    try {
      this.overview = await adminApi.getRevenueOverview();
    } catch (error: any) {
      this.error = error?.message || t('admin.revenue.loadError');
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private render(): void {
    if (!authService.isAuthenticated || !authService.isAdmin) {
      this.innerHTML = `<div class="empty-state">${t('admin.accessDenied')}</div>`;
      return;
    }

    if (this.isLoading) {
      this.innerHTML = `<div class="empty-state">${t('common.loading')}</div>`;
      return;
    }

    if (this.error) {
      this.innerHTML = `<div class="empty-state">${esc(this.error)}</div>`;
      return;
    }

    const overview = this.overview;
    if (!overview) {
      this.innerHTML = `<div class="empty-state">${t('admin.revenue.empty')}</div>`;
      return;
    }

    const rows = overview.items.map((item) => `
      <tr>
        <td>${item.year}</td>
        <td>${formatCurrencyFromCents(item.revenueInCents)}</td>
        <td class="admin-center">${item.invoiceCount}</td>
        <td class="admin-center">${item.creditNoteCount}</td>
      </tr>
    `).join('');

    this.innerHTML = `
      <section class="admin-revenue">
        <div class="admin-revenue-header">
          <div>
            <h2 class="admin-revenue-title">${t('admin.revenue.title')}</h2>
            <p class="admin-revenue-subtitle">${t('admin.revenue.subtitle')}</p>
          </div>
        </div>

        <div class="admin-revenue-cards">
          <article class="admin-revenue-card">
            <span class="admin-revenue-card-label">${t('admin.revenue.totalLabel')}</span>
            <strong class="admin-revenue-card-value">${formatCurrencyFromCents(overview.totalRevenueInCents)}</strong>
          </article>
          <article class="admin-revenue-card">
            <span class="admin-revenue-card-label">${t('admin.revenue.yearsTrackedLabel')}</span>
            <strong class="admin-revenue-card-value">${overview.items.length}</strong>
          </article>
        </div>

        <div class="table-wrap">
          <table class="admin-table admin-revenue-table">
            <thead>
              <tr>
                <th>${t('admin.revenue.col.year')}</th>
                <th>${t('admin.revenue.col.amount')}</th>
                <th>${t('admin.revenue.col.invoices')}</th>
                <th>${t('admin.revenue.col.creditNotes')}</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </section>
    `;
  }
}

if (!customElements.get('rc-admin-revenue')) {
  customElements.define('rc-admin-revenue', RcAdminRevenue);
}
